import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import { TrackManagementService } from '@tsin-core/services/http/track-management.service';
import { tap, catchError } from 'rxjs';
import {
  CreateTracks,
  DeleteTrack,
  GetTracks,
  UpdateTrack,
} from './track-management.actions';
import { ITrack } from '@tsin-core/models/track-management.model';

export interface TrackManagementStateModel {
  tracks: ITrack[];
  loading: LoadingState;
}

@State<TrackManagementStateModel>({
  name: 'trackManagementState',
  defaults: {
    tracks: [],
    loading: LoadingState.notLoading,
  },
})
@Injectable()
export class TrackManagementState {
  trackManagementService: TrackManagementService = inject(
    TrackManagementService
  );

  @Selector()
  static listTracks(state: TrackManagementStateModel): ITrack[] {
    return state?.tracks ?? [];
  }

  @Selector()
  static getLoading(state: TrackManagementStateModel): LoadingState {
    return state.loading;
  }

  @Action(CreateTracks)
  createTracks(
    ctx: StateContext<TrackManagementStateModel>,
    action: CreateTracks
  ) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.trackManagementService.createTrack(action.payload).pipe(
      tap((newTrack: ITrack) => {
        const state = ctx.getState();
        ctx.patchState({
          tracks: [...state.tracks, newTrack],
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetTracks)
  getTracks(ctx: StateContext<TrackManagementStateModel>, action: GetTracks) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.trackManagementService.getTracks().pipe(
      tap((tracks: ITrack[]) => {
        ctx.patchState({ tracks, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(UpdateTrack)
  updateTrack(
    ctx: StateContext<TrackManagementStateModel>,
    action: UpdateTrack
  ) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.trackManagementService.updateTrack(action.editPayload).pipe(
      tap((updatedTrack: ITrack) => {
        const state = ctx.getState();

        const updatedTracks = state.tracks.map((track) =>
          track.id === updatedTrack.id ? updatedTrack : track
        );

        ctx.patchState({
          tracks: updatedTracks,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(DeleteTrack)
  deletetrack(
    ctx: StateContext<TrackManagementStateModel>,
    action: DeleteTrack
  ) {
    ctx.patchState({ loading: LoadingState.loadingDelete });
    return this.trackManagementService.deleteTrack(action.id).pipe(
      tap(() => {
        const state = ctx.getState();
        const updatedTracks = state.tracks.filter(
          (track) => track.id !== action.id
        );
        ctx.patchState({
          tracks: updatedTracks,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }
}
